import React from 'react';
import { StatusModel } from 'er';
import { useStaticGlobalIntl } from 'utils/intl/useStaticGlobalIntl';
import { TruncatedCopy } from 'common/Text/TruncatedCopy';
import { GenderModel } from '@utmost.co/enterprise-models';
import { DataTableTypes } from '@utmost.co/utmost-design-system';
import { useDeepMemo, StatusChipType, StatusChip } from '@beeline.com/bp-design-system';
import { GenderActions } from './GenderActions';
import { useGenderIntl } from './translations/useGenderIntl';
import { GenderAssignable } from './GenderAssignable';

export type GenderColumnType = GenderModel & { '[menu]': string };
export type GenderColumn = DataTableTypes.Column<GenderColumnType>;

interface Props {
  showDeleteModal: (uids: string[]) => void;
  onEditClick: (gender: GenderModel) => void;
  onClickToggleStatus: (uid: string, status: StatusModel) => void;
}
export const useGenderColumns: (props: Props) => GenderColumn[] = ({
  onEditClick,
  showDeleteModal,
  onClickToggleStatus
}) => {
  const [activeLabel, inactiveLabel] = useStaticGlobalIntl('general.Active', 'general.InActive');
  const { name, referenceId, status, assignable } = useGenderIntl();
  return useDeepMemo(
    () => [
      {
        Header: name,
        accessor: 'name'
      },
      {
        Header: status,
        minWidth: 120,
        accessor: 'status',
        Cell: ({ value }) => (
          <span data-test="gender-status">
            <span data-test={value}>
              <StatusChip
                label={value === 'ACTIVE' ? activeLabel : inactiveLabel}
                type={value === 'ACTIVE' ? StatusChipType.LIGHT_BLUE : StatusChipType.GREY}
              />
            </span>
          </span>
        )
      },
      {
        Header: referenceId,
        accessor: 'referenceId',
        ellipsisOnOverflow: false,
        Cell: ({ value }) => <TruncatedCopy textCopy={value ?? '-'} />
      },
      {
        Header: assignable,
        accessor: 'assignable',
        minWidth: 120,
        Cell: ({ value }) => (
          <span data-test="gender-assignable">
            <GenderAssignable assignable={!!value} />
          </span>
        )
      },
      {
        Header: '',
        accessor: '[menu]',
        sticky: 'right',
        Cell: ({ row }) => (
          <GenderActions
            onClickToggleStatus={onClickToggleStatus}
            gender={row.original}
            showDeleteModal={showDeleteModal}
            onEditClick={onEditClick}
          />
        )
      }
    ],
    [
      name,
      referenceId,
      status,
      assignable,
      activeLabel,
      inactiveLabel,
      onClickToggleStatus,
      showDeleteModal,
      onEditClick
    ]
  );
};
